import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  FormControl,
  Typography,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  FormControlLabel,
  Switch,
  styled,
} from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { usePermissions } from '@store/auth';

import ConfirmationDialog from '@components/ConfirmationDialog';

import { showSuccessMessage, showErrorMessage } from '@store/dashboard';
import { selectSelectedMerchant } from '../../store/payment-options-merchants';
import {
  getContextualMessagesMerchantMappingByChannelHash,
  deleteContextualMessagesMerchantMapping,
  selectContextualMessagesMerchantMappingByChannelHash,
  selectIsLoadingContextualMessagesMerchantMappingByChannelHash,
} from '../../store/payment-options-contextual-messages';

const MESSAGES = {
  DeleteDialogTitle: 'Delete contextual messages?',
  DeleteDialogDescription:
    'Are you sure you want to delete the selected contextual messages?',
  DeleteSuccess: 'Yay! Contextual messages has been deleted successfully.',
  DeleteFailure:
    'Oops! Something went wrong while deleting the contextual messages.',
};

const PageContainer = styled(Stack)`
  gap: 16px;
`;

const Toolbar = styled(Stack)`
  flex-direction: row;
  justify-content: space-between;
  gap: 16px;
`;

const DataGridContainer = styled(Stack)`
  height: 418px;
`;

export default function LOBContextualMessages() {
  const dispatch = useDispatch();
  const permissions = usePermissions();

  const lob = useSelector(selectSelectedMerchant);
  const data = useSelector(
    selectContextualMessagesMerchantMappingByChannelHash,
  );
  const isLoading = useSelector(
    selectIsLoadingContextualMessagesMerchantMappingByChannelHash,
  );

  const platforms = lob.merchantDetails;

  const [selectedPlatform, setSelectedPlatform] = useState(
    platforms[0]?.channelHash,
  );
  const [fetchFromDB, setFetchFromDB] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState({ show: false });
  const [deleteRowData, setDeleteRowData] = useState(null);

  useEffect(() => {
    if (!selectedPlatform) return;

    dispatch(
      getContextualMessagesMerchantMappingByChannelHash({
        fetchFromDB,
        channelHash: selectedPlatform,
      }),
    );
  }, [dispatch, selectedPlatform, fetchFromDB]);

  const onPlatformChange = ({ target: { value } }) => {
    setSelectedPlatform(value);
  };

  const onFetchFromDB = () => {
    setFetchFromDB((prev) => !prev);
  };

  const onDelete = (row) => {
    setShowDeleteModal({ show: true });
    setDeleteRowData(row);
  };

  const onDeleteConfirmation = ({ merchantId, bannerId }) => {
    const mappingList = [{ merchantId, bannerId }];

    dispatch(deleteContextualMessagesMerchantMapping({ mappingList })).then(
      ({ payload }) => {
        if (payload && payload.status === 'SUCCESS') {
          dispatch(showSuccessMessage(MESSAGES.DeleteSuccess));
          dispatch(
            getContextualMessagesMerchantMappingByChannelHash({
              fetchFromDB,
              channelHash: selectedPlatform,
            }),
          );
        } else {
          dispatch(
            showErrorMessage({
              message: MESSAGES.DeleteFailure,
              duration: 4000,
            }),
          );
        }
      },
    );
  };

  const renderCell = (line1, line2) => (
    <Box>
      <Typography
        variant="body1"
        sx={{ whiteSpace: 'break-spaces' }}>
        {line1}
      </Typography>
      {line2 && <Typography variant="caption">({line2})</Typography>}
    </Box>
  );

  return (
    <PageContainer>
      <Toolbar>
        <FormControl sx={{ minWidth: '250px' }}>
          <InputLabel id="platform-label">Platform</InputLabel>
          <Select
            labelId="platform-label"
            id="platform"
            value={selectedPlatform}
            label="Platform"
            onChange={onPlatformChange}>
            {platforms.map(
              ({
                channelHash: value,
                platformName: name,
                platformCode: code,
              }) => (
                <MenuItem
                  key={code}
                  value={value}>
                  {`${name} (${code})`}
                </MenuItem>
              ),
            )}
          </Select>
        </FormControl>
        <FormControlLabel
          control={
            <Switch
              checked={fetchFromDB}
              onChange={onFetchFromDB}
            />
          }
          label="Fetch from DB"
        />
      </Toolbar>
      <DataGridContainer>
        <DataGrid
          density="comfortable"
          loading={isLoading}
          rows={data}
          disableSelectionOnClick
          disableColumnFilter
          disableColumnMenu
          disableColumnSorting
          disableColumnSelector
          disableDensitySelector
          pageSize={20}
          rowsPerPageOptions={[10]}
          componentsProps={{
            toolbar: {
              csvOptions: { disableToolbarButton: true },
              printOptions: { disableToolbarButton: true },
              showQuickFilter: false,
              quickFilterProps: { debounceMs: 500 },
            },
          }}
          getRowId={({ code }) => code}
          columns={[
            {
              field: 'bannerName',
              headerName: 'Banner name',
              flex: 3,
              renderCell: ({ row: { bannerName, code } }) =>
                renderCell(bannerName, code),
            },
            {
              field: 'message',
              headerName: 'Banner message',
              sortable: false,
              flex: 5,
              renderCell: ({ row: { message } }) => renderCell(message),
            },
            {
              field: 'bannerType',
              headerName: 'Banner Type',
              flex: 1,
              headerAlign: 'center',
              align: 'center',
              renderCell: ({ row: { bannerType = 'info' } }) =>
                renderCell(bannerType),
            },
            {
              field: 'overridePossible',
              headerName: 'Can LOB override?',
              flex: 2,
              headerAlign: 'center',
              align: 'center',
              renderCell: ({ row: { overridePossible } }) =>
                renderCell(overridePossible === 1 ? 'Yes' : 'No'),
            },
            {
              field: 'action',
              headerName: 'Actions',
              type: 'actions',
              flex: 1,
              getActions: ({ row }) => [
                <GridActionsCellItem
                  key={1}
                  icon={<DeleteIcon />}
                  label="Delete"
                  disabled={!permissions?.canEditContextualMessages}
                  onClick={() => onDelete(row)}
                />,
              ],
            },
          ]}
        />
      </DataGridContainer>
      <ConfirmationDialog
        openDialog={showDeleteModal}
        yesHandler={onDeleteConfirmation}
        data={deleteRowData}
        title={MESSAGES.DeleteDialogTitle}
        message={MESSAGES.DeleteDialogDescription}
      />
    </PageContainer>
  );
}
